<template>
  <div
    :dir="isRtl === true ? 'rtl' : 'ltr'"
    ref="selectorRootRef"
    class="relative w-full entity-type-selector-dropdown-container"
  >
    <button
      type="button"
      class="btn btn-outline w-full justify-start font-normal text-left py-2 px-3 min-h-0"
      :class="{ 'ring-2 ring-primary': isOpen === true }"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen === true"
    >
      <div v-if="selectedTypeObject !== null" class="flex items-center w-full entity-type-selector-trigger-row">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0 entity-type-selector-trigger-icon">
          <Icon :icon="selectedTypeObject.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-grow overflow-hidden entity-type-selector-trigger-copy" :class="isRtl === true ? 'text-right' : 'text-left'">
          <span class="block text-sm font-medium truncate">{{ selectedTypeObject.displayName }}</span>
          <span class="block text-xs text-base-content opacity-70 truncate">{{ selectedTypeObject.description }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 entity-type-selector-trigger-chevron" />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-sm text-base-content opacity-70">{{ t('entityTypeSelector.selectPlaceholder', 'Select entity type') }}</span>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen === true"
        ref="dropdownListRef"
        class="fixed z-[10050] bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 entity-type-options-list flex flex-col"
        :style="dropdownStyle"
        role="listbox"
      >
        <div class="p-2 border-b border-base-300 dark:border-gray-700">
          <input
            v-model="filterQuery"
            type="text"
            class="input input-bordered input-sm w-full"
            :placeholder="t('entityTypeSelector.filterPlaceholder', 'Filter types...')"
            @click.stop
          />
        </div>
        <div class="py-1 overflow-y-auto entity-type-options-scroll">
          <div
            v-for="typeOption in filteredTypeOptions"
            :key="typeOption.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{
              'bg-primary text-primary-content': typeOption.id === resolvedSelectedId,
              'hover:bg-base-200 dark:hover:bg-gray-700': typeOption.id !== resolvedSelectedId
            }"
            @click="selectTypeAndClose(typeOption.id)"
          >
            <div class="p-3">
              <div class="flex items-start entity-type-option-row">
                <div class="flex-shrink-0 entity-type-option-icon-wrap">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600">
                    <Icon :icon="typeOption.icon" class="w-5 h-5 text-base-content" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden entity-type-option-copy" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="typeOption.id === resolvedSelectedId ? 'text-primary-content' : ''">{{ typeOption.displayName }}</h3>
                  <p class="text-xs mt-0.5" :class="typeOption.id === resolvedSelectedId ? 'text-primary-content opacity-80' : 'text-base-content opacity-70'">
                    {{ typeOption.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="filteredTypeOptions.length === 0" class="px-3 py-4 text-sm text-center text-base-content opacity-70">
            {{ t('entityTypeSelector.noMatches', 'No matching types.') }}
          </div>
        </div>
        <div class="border-t border-base-300 dark:border-gray-700 p-2">
          <div
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{
              'bg-primary text-primary-content': resolvedSelectedId === entityCustomTypeOptionId,
              'hover:bg-base-200 dark:hover:bg-gray-700': resolvedSelectedId !== entityCustomTypeOptionId
            }"
            @click="selectCustomTypeMode"
          >
            <div class="p-3">
              <div class="flex items-start entity-type-option-row">
                <div class="flex-shrink-0 entity-type-option-icon-wrap">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600">
                    <Icon icon="tabler:plus" class="w-5 h-5 text-base-content" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden entity-type-option-copy" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight">{{ t('entityTypeSelector.customTypeTitle', 'Custom type') }}</h3>
                  <p class="text-xs mt-0.5 opacity-70">{{ t('entityTypeSelector.customTypeDescription', 'Use your own type key as a string.') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="entityCustomTypeMode === true" class="mt-2 px-1">
            <input
              v-model="entityCustomTypeDraft"
              type="text"
              class="input input-bordered input-sm w-full"
              maxlength="80"
              :placeholder="t('entityTypeSelector.customTypePlaceholder', 'e.g. crmTeam')"
              @keydown.enter.prevent="applyCustomType"
            />
            <button type="button" class="btn btn-sm btn-primary w-full mt-2" @click="applyCustomType">
              {{ t('entityTypeSelector.applyCustomType', 'Apply custom type') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { entityCustomTypeOptionId, entityTypePresets, findEntityTypePreset, isEntityCustomTypeOptionId, normalizeEntityTypeKey } from '@/shared/entityCatalog.js';

const props = defineProps({
  selectedEntityType: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:selectedEntityType', 'preset-selected']);

const isOpen = ref(false);
const filterQuery = ref('');
const entityCustomTypeMode = ref(false);
const entityCustomTypeDraft = ref('');
const selectorRootRef = ref(null);
const dropdownListRef = ref(null);
const dropdownStyle = ref({});
const { t, currentLanguage } = useI18n();

const isRtl = computed(() => {
  if (currentLanguage.value === 'he') {
    return true;
  }
  if (currentLanguage.value === 'ar') {
    return true;
  }
  return false;
});

const presetTypeOptions = computed(() => {
  const entityTypePresetOptions = [];
  for (let entityTypePresetIndex = 0; entityTypePresetIndex < entityTypePresets.length; entityTypePresetIndex = entityTypePresetIndex + 1) {
    const entityTypePreset = entityTypePresets[entityTypePresetIndex];
    const entityTypeLabelEn = typeof entityTypePreset.typeLabels?.en === 'string' ? entityTypePreset.typeLabels.en : entityTypePreset.id;
    const entityTypeLabelHe = typeof entityTypePreset.typeLabels?.he === 'string' ? entityTypePreset.typeLabels.he : entityTypeLabelEn;
    const entityTypeDisplayName = currentLanguage.value === 'he' ? entityTypeLabelHe : entityTypeLabelEn;
    entityTypePresetOptions.push({
      id: entityTypePreset.id,
      icon: entityTypePreset.icon,
      displayName: entityTypeDisplayName,
      description: entityTypePreset.id
    });
  }
  return entityTypePresetOptions;
});

const filteredTypeOptions = computed(() => {
  const entityTypeFilterQuery = typeof filterQuery.value === 'string' ? filterQuery.value.trim().toLowerCase() : '';
  const entityTypePresetOptions = presetTypeOptions.value;
  if (entityTypeFilterQuery === '') {
    return entityTypePresetOptions;
  }
  const filteredEntityTypeOptions = [];
  for (let entityTypeOptionIndex = 0; entityTypeOptionIndex < entityTypePresetOptions.length; entityTypeOptionIndex = entityTypeOptionIndex + 1) {
    const entityTypeOption = entityTypePresetOptions[entityTypeOptionIndex];
    const entityTypeSearchText = `${entityTypeOption.id} ${entityTypeOption.displayName} ${entityTypeOption.description}`.toLowerCase();
    if (entityTypeSearchText.indexOf(entityTypeFilterQuery) > -1) {
      filteredEntityTypeOptions.push(entityTypeOption);
    }
  }
  return filteredEntityTypeOptions;
});

const resolvedSelectedId = computed(() => {
  const normalizedSelectedEntityType = normalizeEntityTypeKey(props.selectedEntityType);
  if (normalizedSelectedEntityType === '') {
    return '';
  }
  const entityTypePreset = findEntityTypePreset(normalizedSelectedEntityType);
  if (entityTypePreset !== null) {
    return entityTypePreset.id;
  }
  return entityCustomTypeOptionId;
});

const selectedTypeObject = computed(() => {
  const resolvedEntityTypeOptionId = resolvedSelectedId.value;
  if (resolvedEntityTypeOptionId === '') {
    return null;
  }
  if (isEntityCustomTypeOptionId(resolvedEntityTypeOptionId) === true) {
    const normalizedCustomEntityType = normalizeEntityTypeKey(props.selectedEntityType);
    if (normalizedCustomEntityType === '') {
      return null;
    }
    return {
      id: normalizedCustomEntityType,
      icon: 'tabler:plus',
      displayName: normalizedCustomEntityType,
      description: t('entityTypeSelector.customTypeApplied', 'Custom type')
    };
  }
  for (let entityTypeOptionIndex = 0; entityTypeOptionIndex < presetTypeOptions.value.length; entityTypeOptionIndex = entityTypeOptionIndex + 1) {
    if (presetTypeOptions.value[entityTypeOptionIndex].id === resolvedEntityTypeOptionId) {
      return presetTypeOptions.value[entityTypeOptionIndex];
    }
  }
  return null;
});

function positionDropdown() {
  if (selectorRootRef.value === null || selectorRootRef.value === undefined) {
    return;
  }
  if (dropdownListRef.value === null || dropdownListRef.value === undefined) {
    return;
  }
  const triggerRect = selectorRootRef.value.getBoundingClientRect();
  const dropdownMaxHeight = 420;
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  let topPosition = triggerRect.bottom + 4;
  if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow && spaceAbove > dropdownMaxHeight) {
    topPosition = triggerRect.top - dropdownListRef.value.offsetHeight - 4;
    if (topPosition < 4) {
      topPosition = 4;
    }
  }
  dropdownStyle.value = {
    position: 'fixed',
    width: `${triggerRect.width}px`,
    left: `${triggerRect.left}px`,
    top: `${topPosition}px`,
    maxHeight: `${dropdownMaxHeight}px`
  };
}

async function toggleDropdown() {
  if (isOpen.value === true) {
    isOpen.value = false;
    return;
  }
  isOpen.value = true;
  await nextTick();
  positionDropdown();
  await nextTick();
  positionDropdown();
}

function closeDropdown() {
  isOpen.value = false;
  filterQuery.value = '';
}

function emitPresetSelected(entityTypeKey) {
  const entityTypePreset = findEntityTypePreset(entityTypeKey);
  if (entityTypePreset !== null) {
    emit('preset-selected', entityTypePreset);
  }
}

function selectTypeAndClose(entityTypeOptionId) {
  if (props.selectedEntityType !== entityTypeOptionId) {
    emit('update:selectedEntityType', entityTypeOptionId);
    emitPresetSelected(entityTypeOptionId);
  }
  entityCustomTypeMode.value = false;
  closeDropdown();
}

function selectCustomTypeMode() {
  entityCustomTypeMode.value = true;
  if (props.selectedEntityType !== '') {
    const entityTypePreset = findEntityTypePreset(props.selectedEntityType);
    if (entityTypePreset === null) {
      entityCustomTypeDraft.value = props.selectedEntityType;
    }
  }
}

function applyCustomType() {
  const normalizedCustomEntityType = normalizeEntityTypeKey(entityCustomTypeDraft.value);
  if (normalizedCustomEntityType === '') {
    return;
  }
  if (props.selectedEntityType !== normalizedCustomEntityType) {
    emit('update:selectedEntityType', normalizedCustomEntityType);
  }
  entityCustomTypeMode.value = false;
  closeDropdown();
}

function handleClickOutside(event) {
  if (isOpen.value !== true) {
    return;
  }
  const rootElement = selectorRootRef.value;
  const menuElement = dropdownListRef.value;
  if (rootElement !== null && rootElement !== undefined && rootElement.contains(event.target) === true) {
    return;
  }
  if (menuElement !== null && menuElement !== undefined && menuElement.contains(event.target) === true) {
    return;
  }
  closeDropdown();
}

watch(() => props.selectedEntityType, (selectedEntityTypeValue) => {
  const normalizedSelectedEntityType = normalizeEntityTypeKey(selectedEntityTypeValue);
  if (normalizedSelectedEntityType === '') {
    entityCustomTypeMode.value = false;
    return;
  }
  const entityTypePreset = findEntityTypePreset(normalizedSelectedEntityType);
  if (entityTypePreset === null) {
    entityCustomTypeDraft.value = normalizedSelectedEntityType;
  }
});

watch(isOpen, (open) => {
  if (open === true) {
    window.addEventListener('resize', positionDropdown);
    window.addEventListener('scroll', positionDropdown, true);
  } else {
    window.removeEventListener('resize', positionDropdown);
    window.removeEventListener('scroll', positionDropdown, true);
  }
});

watch(entityCustomTypeMode, () => {
  if (isOpen.value === true) {
    nextTick(() => {
      positionDropdown();
    });
  }
});

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('resize', positionDropdown);
  window.removeEventListener('scroll', positionDropdown, true);
});
</script>

<style scoped>
.entity-type-options-list {
  max-height: 420px;
}

.entity-type-options-scroll {
  max-height: 280px;
}

.entity-type-selector-trigger-row > * {
  margin-right: 0.5rem;
}

.entity-type-selector-trigger-icon {
  margin-right: 0;
}

.entity-type-selector-trigger-copy {
  margin-left: 0;
  margin-right: 0.5rem;
}

.entity-type-option-row > * {
  margin-right: 0.75rem;
}

.entity-type-option-icon-wrap {
  margin-right: 0;
}

.entity-type-option-copy {
  margin-left: 0;
}
</style>
